import { Injectable } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationModalComponent } from 'src/app/shared/components/modals/confirmation-modal/confirmation-modal.component';
import { Column } from '../../models/column.model';

export interface ModalOptions extends NgbModalOptions {
    submitText?: string;
    cancelText?: string;
    confirmText?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ModalService {
    constructor(private ngbModal: NgbModal) {}

    /**
     * Ouvre une modale
     * @param content - Contenu de la modale
     * @param options - Options de configuration de la modale
     */
    open(content: any, options?: NgbModalOptions): NgbModalRef {
        return this.ngbModal.open(content, options);
    }

    /**
     * Ouvre une modale de confirmation
     * @param message - Message à afficher dans la modale
     * @param title - Titre de la modale
     * @param options - Options de configuration de la modale
     */
    confirm(message: string, title: string = 'Confirmation', options: ModalOptions = {}): Promise<any> {
        const modalRef = this.ngbModal.open(ConfirmationModalComponent, options);
        modalRef.componentInstance.title = title;
        modalRef.componentInstance.message = message;
        modalRef.componentInstance.confirmText = options.submitText || options.confirmText || 'Valider';
        modalRef.componentInstance.cancelText = options.cancelText || 'Annuler';
        modalRef.componentInstance.showCancel = true;
        modalRef.componentInstance.confirmButtonClass = 'btn-primary';
        return modalRef.result;
    }

    /**
     * Ouvre une modale d'alerte
     * @param message - Message à afficher dans la modale
     * @param title - Titre de la modale
     * @param options - Options de configuration de la modale
     */
    alert(message: string, title: string = 'Alerte', options: ModalOptions = {}): Promise<any> {
        const modalRef = this.ngbModal.open(ConfirmationModalComponent, options);
        modalRef.componentInstance.title = title;
        modalRef.componentInstance.message = message;
        modalRef.componentInstance.confirmText = options.submitText || options.confirmText || 'OK';
        modalRef.componentInstance.showCancel = false;
        modalRef.componentInstance.confirmButtonClass = 'btn-primary';
        return modalRef.result;
    }

    /**
     * Ouvre une modale d'erreur
     * @param message - Message à afficher dans la modale
     * @param title - Titre de la modale
     * @param options - Options de configuration de la modale
     */
    error(message: string, title: string = 'Erreur', options: ModalOptions = {}): Promise<any> {
        const modalRef = this.ngbModal.open(ConfirmationModalComponent, options);
        modalRef.componentInstance.title = title;
        modalRef.componentInstance.message = message;
        modalRef.componentInstance.confirmText = options.submitText || options.confirmText || 'OK';
        modalRef.componentInstance.showCancel = false;
        modalRef.componentInstance.confirmButtonClass = 'btn-danger';
        return modalRef.result;
    }

    /**
     * Charge la visibilité des colonnes à partir du stockage local
     * @param storageKey - Clé de stockage pour les préférences de visibilité des colonnes
     * @param columns - Liste des colonnes pour lesquelles la visibilité doit être chargée
     */
    loadColumnVisibility(storageKey: string, columns: Column[]): Column[] {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            const parsed = JSON.parse(saved);
            for (const col of columns) {
                const savedCol = parsed.find((c: any) => c.field === col.field);
                if (savedCol) {
                    col.visible = savedCol.visible;
                }
            }
        }
        return columns;
    }

    /**
     * Récupère le nombre de colonnes visibles dans une liste de colonnes
     * @param columns - Liste des colonnes pour lesquelles le nombre de colonnes visibles doit être calculé
     */
    getVisibleColumnCount(columns: Column[]): number {
        return columns.filter(col => col.visible).length;
    }
}
